import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Col, Modal, Row, Select } from 'antd';

import Alert from 'components/Alert/Alert';
import HTMLText from 'components/HTMLText/HTMLText';
import LoadingSpinner from 'components/LoadingSpinner';
import OptionJobWithLocation from 'components/OptionJobWithLoaction/OptionJobWithLocation';

import jobsService from 'features/jobs/jobsService';
import jobseekersService from 'features/jobseekers/jobseekersService';

import useErrorCatch from 'hooks/useErrorCatch';

import { GTMEventLogger } from 'utils/analytics';
import handleSearchJobOptionsWithLocation from 'utils/handleSearchJobOptionsWithLocation';
import { notify } from 'utils/toast/createToast';

import { Job } from 'types/jobs';
import { Jobseeker } from 'types/jobseekers';

import { AlertType, GTMEvents } from 'constants/enums';
import { SECTION_ROW_GUTTER } from 'constants/uiSettings';

import styles from './InviteJobseekerToJobModal.module.scss';

const MODAL_WIDTH = '550px';

interface InviteJobseekerToJobModalProps {
  jobseekerId: string;
  onCancel: () => void;
}

const InviteJobseekerToJobModal: FC<InviteJobseekerToJobModalProps> = ({
  jobseekerId,
  onCancel
}: InviteJobseekerToJobModalProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [jobseeker, setJobseeker] = useState<Jobseeker | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);

  const FALLBACK_JOB_TITLE = t('common.job');
  const FALLBACK_JOBSEEKER_NAME = t('common.jobseeker');

  const catchError = useErrorCatch();

  const jobName =
    jobs.find((el) => el.id === selectedJobId)?.title || FALLBACK_JOB_TITLE;
  const jobseekerName = jobseeker?.fullName || FALLBACK_JOBSEEKER_NAME;

  const loadData = async () => {
    try {
      setIsLoading(true);
      const jobseeker = await jobseekersService.getJobseekerById(jobseekerId);

      const jobs = await jobsService.getActiveJobs();

      setJobseeker(jobseeker);
      setJobs(jobs);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.inviteJobseeker')
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const jobsOptions = useMemo(() => {
    return jobs.map((job: Job) => ({
      label: <OptionJobWithLocation job={job} />,
      value: job.id,
      location: job.location,
      name: job.title
    }));
  }, [jobs]);

  const handleChangeJob = (value: string) => {
    setSelectedJobId(value);
  };

  const handleInviteJobseekerToJob = async () => {
    if (!selectedJobId) {
      return;
    }

    try {
      setIsApplying(true);
      await jobseekersService.sendJobInviteToJobseeker(
        jobseekerId,
        selectedJobId
      );

      notify({
        type: AlertType.Success,
        message: t('common.operationSuccessful'),
        description: t('modal.jobseekers.invite.success', {
          name: jobseekerName,
          jobname: jobName
        })
      });

      GTMEventLogger(GTMEvents.JobseekersListInviteSent);

      onCancel();
    } catch (error) {
      GTMEventLogger(GTMEvents.JobseekersListInviteFailed);

      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.inviteJobseeker', {
            name: jobseekerName,
            jobname: jobName
          })
        }
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Modal
      className={styles.container}
      open
      onCancel={onCancel}
      footer={null}
      width={MODAL_WIDTH}
      title={t('modal.jobseekers.title')}
      destroyOnHidden
    >
      <>
        {isLoading ? (
          <LoadingSpinner isFullHeight={false} className={styles.spinner} />
        ) : (
          <Row gutter={SECTION_ROW_GUTTER}>
            <Col span={24}>
              <Alert
                type={AlertType.Warning}
                message={
                  <>
                    {jobsOptions?.length ? (
                      <span>
                        <HTMLText
                          text={t('modal.jobseekers.inviteToJob', {
                            name: jobseekerName
                          })}
                        />
                      </span>
                    ) : (
                      t('modal.jobseekers.noJobs')
                    )}
                  </>
                }
              />
            </Col>

            <Col span={24}>
              <>
                <div className={styles.select}>
                  <Select
                    options={jobsOptions}
                    onChange={handleChangeJob}
                    value={selectedJobId}
                    placeholder={t('modal.jobseekers.selectJob')}
                    disabled={isApplying}
                    showSearch
                    filterOption={handleSearchJobOptionsWithLocation}
                  />
                </div>
              </>
            </Col>

            <Col span={24}>
              <div className={styles.buttons}>
                <Button
                  onClick={onCancel}
                  className={styles.buttonCancel}
                  disabled={isApplying}
                  danger
                >
                  {t('common.cancel')}
                </Button>

                <Button
                  type="primary"
                  onClick={handleInviteJobseekerToJob}
                  disabled={!selectedJobId || isApplying}
                  loading={isApplying}
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </>
    </Modal>
  );
};

export default InviteJobseekerToJobModal;
